import {PaginationConfig} from 'antd/lib/pagination';
import {
  ImportReportErrorTableFiltersFE,
  ImportReportErrorTableSorters,
  ImportReportOkTableFiltersFE,
  ImportReportOkTableSorters,
  ImportReportWarningTableFiltersFE,
  ImportReportWarningTableSorters,
} from '../../types/internal/ImportReportTable';
import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from '../store';
import {ERROR_STATUS, OK_STATUS, REPORT_DETAIL_STATUS, REPORT_STATUS, WARNING_STATUS} from '../../utils/dataImport';
import {cloneDeep} from 'lodash';

interface ImportReportSlice {
  [REPORT_STATUS.ERROR]: {
    filters: ImportReportErrorTableFiltersFE,
    sorters: ImportReportErrorTableSorters,
    pagination: PaginationConfig,
  },
  [REPORT_STATUS.WARNING]: {
    filters: ImportReportWarningTableFiltersFE,
    sorters: ImportReportWarningTableSorters,
    pagination: PaginationConfig,
  },
  [REPORT_STATUS.OK]: {
    filters: Omit<ImportReportOkTableFiltersFE, 'is_resolved'>,
    sorters: ImportReportOkTableSorters,
    pagination: PaginationConfig,
  }
}

/*Sorters*/
export const DEFAULT_IMPORT_REPORT_ERRORS_TABLE_SORTERS: ImportReportErrorTableSorters = {
  sigi_code: null,
  country: null,
  admin_level_1: null,
  admin_level_2: null,
  admin_level_3: null,
  address: null,
  operation: null,
  is_resolved: null,
};
export const DEFAULT_IMPORT_REPORT_WARNINGS_TABLE_SORTERS: ImportReportWarningTableSorters = {
  sigi_code: null,
  country: null,
  admin_level_1: null,
  admin_level_2: null,
  admin_level_3: null,
  address: null,
  operation: null,
  is_resolved: null,
};
export const DEFAULT_IMPORT_REPORT_OK_SORTERS: ImportReportOkTableSorters = {
  sigi_code: null,
  country: null,
  admin_level_1: null,
  admin_level_2: null,
  admin_level_3: null,
  address: null,
  operation: null,
};

const initialState: ImportReportSlice = {
  [REPORT_STATUS.ERROR]: {
    filters: {},
    sorters: DEFAULT_IMPORT_REPORT_ERRORS_TABLE_SORTERS,
    pagination: {
      current: 1,
      pageSize: +(window.sessionStorage.getItem('import_report_errors_page_size') || 10),
    },
  },
  [REPORT_STATUS.WARNING]: {
    filters: {},
    sorters: DEFAULT_IMPORT_REPORT_WARNINGS_TABLE_SORTERS,
    pagination: {
      current: 1,
      pageSize: +(window.sessionStorage.getItem('import_report_warnings_page_size') || 10),
    },
  },
  [REPORT_STATUS.OK]: {
    filters: {},
    sorters: DEFAULT_IMPORT_REPORT_OK_SORTERS,
    pagination: {
      current: 1,
      pageSize: +(window.sessionStorage.getItem('import_report_ok_page_size') || 10),
    },
  },
};

export const importReportSlice = createSlice({
  name: 'importReport',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    
    setImportReportSorters: (state, action: PayloadAction<
      (Partial<ImportReportErrorTableSorters> & { status: ERROR_STATUS }) |
      (Partial<ImportReportWarningTableSorters> & { status: WARNING_STATUS }) |
      (Partial<ImportReportOkTableSorters> & { status: OK_STATUS })>,
    ) => {
      const {status, ...payload} = action.payload;
      state[status].sorters = {...state[status].sorters, ...payload};
    },
    
    setImportReportFilters: (state, action: PayloadAction<
      (Partial<ImportReportErrorTableFiltersFE> & { status: ERROR_STATUS }) |
      (Partial<ImportReportWarningTableFiltersFE> & { status: WARNING_STATUS }) |
      (Partial<ImportReportOkTableFiltersFE> & { status: OK_STATUS })>,
    ) => {
      const {status, ...payload} = action.payload;
      state[status].filters = payload;
      state[status].pagination.current = 1;
    },
    
    setImportReportPagination: (state, action: PayloadAction<Partial<PaginationConfig> & {
      status: REPORT_DETAIL_STATUS
    }>) => {
      const {status, ...payload} = action.payload;
      switch (status) {
        case REPORT_STATUS.ERROR:
          break;
      }
      state[status].pagination = {...state[status].pagination, ...payload};
      if (payload.pageSize) {
        switch (status) {
          case 'ERROR':
            window.sessionStorage.setItem('import_report_errors_page_size', `${payload.pageSize}`);
            break;
          case 'WARNING':
            window.sessionStorage.setItem('import_report_warnings_page_size', `${payload.pageSize}`);
            break;
          case 'OK':
            window.sessionStorage.setItem('import_report_ok_page_size', `${payload.pageSize}`);
            break;
        }
      }
    },
    removeImportReportFilter: (state, action: PayloadAction<
      { status: ERROR_STATUS | WARNING_STATUS, filter: keyof (ImportReportErrorTableFiltersFE | ImportReportWarningTableFiltersFE) } |
      { status: OK_STATUS, filter: keyof Omit<ImportReportOkTableFiltersFE, 'is_resolved'> }
    >) => {
      const {status, filter} = action.payload;
      switch (status) {
        case REPORT_STATUS.ERROR:
        case REPORT_STATUS.WARNING:
          const cloneFilters = cloneDeep(state[status].filters);
          delete cloneFilters[filter];
          state[status].filters = cloneFilters;
          break;
        case REPORT_STATUS.OK:
          const okFilters = cloneDeep(state[status].filters);
          delete okFilters[filter];
          state[status].filters = okFilters;
          break;
      }
      state[status].pagination.current = 1;
    },
  },
});

export const {
  setImportReportSorters,
  removeImportReportFilter,
  setImportReportFilters,
  setImportReportPagination,
} = importReportSlice.actions;

export const selectImportReport = (state: RootState) => state.importReport;

export default importReportSlice.reducer;
